import { useEffect, useMemo, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { Dial } from './components/Dial';
import { InputsPanel } from './components/InputsPanel';
import { ResultPanel } from './components/ResultPanel';
import { ReturnLink } from './components/ReturnLink';
import { recommend, reasoning } from './lib/score';
import type { Inputs } from './data/types';
import { DEFAULT_INPUTS } from './data/types';
import styles from './App.module.css';

const INPUTS_KEY = 'dose-calibrator:inputs';

export function App() {
  const [inputs, setInputs] = useState<Inputs>(() =>
    storage.get(INPUTS_KEY, DEFAULT_INPUTS)
  );

  useEffect(() => {
    storage.set(INPUTS_KEY, inputs);
  }, [inputs]);

  const recommendation = useMemo(() => recommend(inputs), [inputs]);
  const reasoningParagraphs = useMemo(
    () => reasoning(inputs, recommendation),
    [inputs, recommendation]
  );

  function reset() {
    if (confirm('reset all inputs?')) {
      setInputs(DEFAULT_INPUTS);
    }
  }

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        <button type="button" className={styles.reset} onClick={reset}>
          reset
        </button>
      </header>

      <main className={styles.stage}>
        <header className={styles.hero}>
          <h1 className={styles.heading}>the dose calibrator</h1>
          <p className={styles.subtitle}>an honest read on what scale fits your life right now</p>
        </header>

        <div className={styles.layout}>
          <aside className={styles.dialColumn}>
            <Dial dose={recommendation.dose} score={recommendation.score} />
          </aside>

          <section className={styles.inputsColumn}>
            <h2 className={styles.sectionTitle}>your inputs</h2>
            <InputsPanel inputs={inputs} onChange={setInputs} />
          </section>

          <section className={styles.resultColumn}>
            <ResultPanel
              recommendation={recommendation}
              reasoningParagraphs={reasoningParagraphs}
            />
          </section>
        </div>
      </main>

      <footer className={styles.footer}>
        <p>
          this is a starting point for reflection, not a verdict. revisit it
          when life shifts.
        </p>
      </footer>
    </>
  );
}
